import React, { useState, useEffect } from 'react'
import { Button } from '~common/components/button'
import { Icon } from '~common/components/icon'
import { SettingsModal } from '~/features/settings/ui'
import { useAppContext } from '~/app-context'
import { useCacheValue } from '~/cache-context'
import DropdownMenu from '~common/components/dropdown-menu'
import styled from 'styled-components'

export interface SettingsButtonProps {
    showLabel?: boolean
}

export const SettingsButton: React.FC<SettingsButtonProps> = ({
    showLabel,
}) => {
    const appContext = useAppContext()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [profileBase64, setProfileBase64] = useState<string | null>(null)

    const authState = useCacheValue('authState')
    const userEmail = authState?.user?.email || ''

    const { storageModules } = appContext

    useEffect(() => {
        if (userEmail && storageModules) {
            storageModules.profilePictures
                .getProfilePictureBase64(userEmail)
                .then((base64) => setProfileBase64(base64))
                .catch((error) => {
                    console.error('Failed to fetch profile picture:', error)
                    setProfileBase64(null)
                })
        }
    }, [userEmail, storageModules.auth])

    const handleOpenModal = () => {
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
    }

    const handleLogout = async () => {
        await appContext.services.auth.logout()
        window.location.reload()
    }

    const dropdownItems = [
        {
            id: 'settings',
            label: 'Settings',
            icon: 'gear',
            onClick: handleOpenModal,
        },
        {
            id: 'logout',
            label: 'Logout',
            icon: 'user',
            onClick: handleLogout,
        },
    ]

    const triggerIcon = profileBase64 ? (
        <ProfileImage
            src={`data:image/jpeg;base64,${profileBase64}`}
            alt="Profile picture"
            className="w-6 h-6 rounded-full object-cover"
        />
    ) : (
        <Icon name="gear" size="medium" />
    )

    const label = showLabel ? 'Settings' : undefined

    return (
        <SettingsButtonContainer>
            <DropdownMenu
                trigger={
                    <SettingsButtonButton>
                        {triggerIcon}
                        <span>{label}</span>
                    </SettingsButtonButton>
                }
                items={dropdownItems}
            />
            <SettingsModal
                isOpen={isModalOpen}
                onOpen={handleOpenModal}
                onClose={handleCloseModal}
            />
        </SettingsButtonContainer>
    )
}

const SettingsButtonButton = styled.div`
    width: 100%;
    box-sizing: border-box;
    padding: ${({ theme }) => theme.spacing[2]};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 50px;
    width: 100%;
    box-sizing: border-box;
    padding: ${({ theme }) => theme.spacing[2]};
    border-radius: ${({ theme }) => theme.borderRadius.full};
    color: ${({ theme }) => theme.colors.greyScale[50]};
    gap: ${({ theme }) => theme.spacing[3]};
    cursor: pointer;
    &:hover {
        background-color: ${({ theme }) => theme.colors.greyScale[400]};
    }
`

const ProfileImage = styled.img`
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
`

const SettingsButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    width: calc(100% + ${({ theme }) => theme.spacing[4]});
    box-sizing: border-box;
    padding: ${({ theme }) => theme.spacing[2]};
    margin-left: -${({ theme }) => theme.spacing[2]};
`
